/**
 * Which follow-up queue the screen opens on.
 *
 * WHY THE TAB IS REMEMBERED AT ALL
 * --------------------------------
 * `FollowUps` is worked through one contact at a time: open the person, log
 * the touch, come back for the next one. With an uncontrolled `<Tabs
 * defaultValue={QUEUES[0].value}>` the screen snapped back to Overdue on every
 * one of those returns, so anybody working the Stalled queue re-selected it
 * after every single contact. That is the papercut atomic#13 describes, and
 * the queue tab is precisely the kind of state it names: a choice the user
 * made about how they want to see the screen, not a dialog flag or a draft.
 *
 * The app already runs a persisted store -- `localStorageStore(undefined,
 * "CRM")` at root/CRM.tsx:70 -- and `useStore` from ra-core writes to it with
 * `useState`'s signature. `LifecycleIndex` uses it for its filter box on the
 * same grounds.
 *
 * WHY A STORED VALUE MUST BE CHECKED BEFORE IT IS USED
 * ---------------------------------------------------
 * This is the half that makes persistence different from state. `useState`
 * can only ever hold a value this build put there. A store holds whatever the
 * build that wrote it put there, months ago, in a browser that has since been
 * updated -- so the moment a queue is renamed, split or dropped, some real
 * person's localStorage names a queue that no longer exists.
 *
 * Radix `Tabs` handles that by rendering NOTHING: `value="snoozed"` lights no
 * trigger and shows no `TabsContent`, so the screen is tab bar, blank space,
 * and no way to tell whether the queues are empty or the page is broken --
 * which is the exact confusion the rest of this screen is built to avoid, and
 * it would arrive only for the users who had chosen that queue.
 *
 * So the stored value is a SUGGESTION, checked against the queues that exist
 * now, every render. It is deliberately not repaired in the store: rewriting
 * somebody's preference because this build does not recognize it discards
 * information a later build might, and the first click corrects it anyway.
 */

/**
 * Namespaced per screen, as `data-table.tsx` does with `storeKey`.
 *
 * The store is shared by every screen in the app and keyed by nothing but
 * this string, so a bare "queue" would be claimed by whichever screen mounted
 * last -- and both would then read a value the other wrote.
 */
export const FOLLOW_UPS_QUEUE_KEY = "followups.queue";

/**
 * The queue to show: the stored one if it is still a queue, else the first.
 *
 * `stored` is typed `unknown` on purpose. `useStore<string>` types the
 * ANNOTATION, not the data -- what comes back is whatever `JSON.parse` made
 * of the string in localStorage, so a hand-edited or half-written entry can
 * be a number, an object or null, and none of those are `string` however the
 * call site is annotated.
 *
 * Falling back to `queues[0]` rather than to a named queue keeps this
 * agreeing with the screen it serves: Overdue is first because it is the most
 * urgent, and if that order ever changes the fallback follows it.
 */
export function queueToShow<T extends string>(
  stored: unknown,
  queues: readonly T[],
): T {
  return queues.includes(stored as T) ? (stored as T) : queues[0];
}
